/**
 * ModelosPanel — GEN-MO-009: el Owner ve el catálogo de modelos de su instalación y qué modelo usa cada
 * clase de llamada, y lo cambia. Solo admin: el ítem `modelos` lo proyecta el kernel y la puerta real es
 * GET/PUT /v1/admin/modelos (403 al resto). El kernel decide el modelo efectivo y su origen; aquí solo se
 * pintan. Las filas salen de `clases` (construidas desde las claves de la política): una clase futura
 * aparece sin tocar este fichero. El aviso de coste NO bloquea: decide el Owner.
 */

import { useEffect, useState } from 'react'
import { getModelos, guardarModelos } from '../api/client.js'

const POR_DEFECTO = ''  // valor del selector para «por defecto de la credencial» (null en el kernel)
const AVISO_HAIKU_JUEZ = 'Haiku no pasó la puerta de calidad como juez (GEN-MO-005): puede dejar pasar incumplimientos.'

const num = v => Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 })
const tarifaTxt = t => `${num(t.entrada_usd_millon)} / ${num(t.salida_usd_millon)} USD por millón de tokens (entrada / salida)`
const cuando = iso => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-ES', { timeZone: 'UTC' }) + ' UTC'
}

function avisoCoste(datos, clase, elegido) {
  // Modelo que quedaría: el elegido, o el de la credencial si se deja «por defecto».
  const nuevo = elegido === POR_DEFECTO ? datos.default_credencial : elegido
  const actual = clase.efectivo
  const t = m => datos.catalogo.find(x => x.model === m)?.tarifa
  const avisos = []
  if (nuevo && nuevo !== actual && t(nuevo)) {
    const base = actual && t(actual) ? ` Ahora: ${tarifaTxt(t(actual))} con ${actual}.` : ''
    avisos.push(`Coste de ${nuevo}: ${tarifaTxt(t(nuevo))}${t(nuevo).estimada ? ' (tarifa estimada: sin tarifa publicada)' : ''}.${base}`)
  }
  if (clase.clave === 'juez' && nuevo?.startsWith('claude-haiku')) avisos.push(AVISO_HAIKU_JUEZ)
  return avisos
}

export default function ModelosPanel() {
  const [datos, setDatos] = useState(null)
  const [sel, setSel] = useState({})
  const [estado, setEstado] = useState({ guardando: false, error: null, ok: false })

  const aplicar = d => {
    setDatos(d)
    setSel(Object.fromEntries(d.clases.map(c => [c.clave, c.configurado ?? POR_DEFECTO])))
  }

  useEffect(() => {
    let vivo = true
    getModelos()
      .then(d => vivo && aplicar(d))
      .catch(() => vivo && setDatos({ error: 'No se ha podido cargar el catálogo de modelos.' }))
    return () => { vivo = false }
  }, [])

  if (!datos) return <section className="mec-visor"><h2>Modelos</h2><p>Cargando…</p></section>
  if (datos.error) {
    return <section className="mec-visor"><h2>Modelos</h2><div className="mec-error" role="alert">{datos.error}</div></section>
  }

  const cambios = Object.fromEntries(
    datos.clases
      .filter(c => sel[c.clave] !== (c.configurado ?? POR_DEFECTO))
      .map(c => [c.clave, sel[c.clave] === POR_DEFECTO ? null : sel[c.clave]]),
  )
  const hayCambios = Object.keys(cambios).length > 0

  const guardar = async () => {
    setEstado({ guardando: true, error: null, ok: false })
    try {
      aplicar(await guardarModelos(cambios))
      setEstado({ guardando: false, error: null, ok: true })
    } catch (e) {
      setEstado({ guardando: false, error: e?.message || 'No se ha podido guardar.', ok: false })
    }
  }

  const { politica } = datos
  const seleccionables = datos.catalogo.filter(m => m.seleccionable)

  return (
    <section className="mec-visor">
      <h2>Modelos</h2>
      <p className="coste-nota">
        Modelos que admite tu credencial y qué modelo usa cada clase de llamada. El cambio vale desde el siguiente turno.
      </p>

      <h3>Catálogo</h3>
      <table className="mec-tabla">
        <thead>
          <tr><th>Proveedor</th><th>Modelo</th><th>Tarifa (USD / M tokens, entrada / salida)</th></tr>
        </thead>
        <tbody>
          {datos.catalogo.map(m => (
            <tr key={`${m.provider}|${m.model}`}>
              <td>{m.provider}</td>
              <td>
                {m.model} {m.default && <span className="coste-marca">por defecto de la credencial</span>}
              </td>
              <td>
                {num(m.tarifa.entrada_usd_millon)} / {num(m.tarifa.salida_usd_millon)}{' '}
                {m.tarifa.estimada && <span className="coste-marca">estimada</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Clases de llamada</h3>
      {datos.clases.map(c => {
        const avisos = avisoCoste(datos, c, sel[c.clave])
        const id = `modelo-${c.clave}`
        return (
          <div className="modelos-clase" key={c.clave}>
            <div className="modelos-clase__cab">
              <label htmlFor={id}><strong>{c.etiqueta}</strong></label>
              <span className="coste-nota">
                Efectivo: {c.efectivo ?? '—'} · {c.origen_texto}
              </span>
            </div>
            <select id={id} value={sel[c.clave]} onChange={e => { setSel({ ...sel, [c.clave]: e.target.value }); setEstado(s => ({ ...s, ok: false })) }}>
              <option value={POR_DEFECTO}>Por defecto de la credencial</option>
              {seleccionables.map(m => <option key={m.model} value={m.model}>{m.model}</option>)}
            </select>
            {avisos.map(a => <p key={a} className="modelos-aviso" role="status">{a}</p>)}
          </div>
        )
      })}

      <div className="modelos-acciones">
        <button type="button" onClick={guardar} disabled={!hayCambios || estado.guardando}>
          {estado.guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {estado.ok && <span className="modelos-ok" role="status">Guardado. Vale desde el siguiente turno.</span>}
      </div>
      {estado.error && <div className="mec-error" role="alert">{estado.error}</div>}

      <p className="coste-nota">
        {politica.actualizado_por
          ? `Última modificación: ${politica.actualizado_por} · ${cuando(politica.actualizado_en)}`
          : 'Última modificación: sin cambios desde el Pórtico.'}
      </p>
    </section>
  )
}
